import React, { useId, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import TextField from "components/form/common/field/textField"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  InviteCodeField,
  InviteCodeState,
  inviteCodeFields,
} from "ts/types/InviteCode"
import { regexAlphaNumeric, regexAlphaNumericKey } from "utils/constants"

interface IProps {
  label?: string
  setInviteCode: React.Dispatch<React.SetStateAction<InviteCodeState>>
}

const sanitizeInviteCode = (value: string) => value.replace(regexAlphaNumeric, "")

const ClassCode = ({ label, setInviteCode }: IProps) => {
  const methods = useFormContext()
  const { t } = useTranslation()
  const labelId = useId()
  const [pasteAnnouncement, setPasteAnnouncement] = useState("")

  const handleGetNumber = (value: string, fieldName: InviteCodeField) => {
    setInviteCode((prev) => ({
      ...prev,
      [fieldName]: value,
    }))

    if (value.length === 1) {
      const currentIdx = inviteCodeFields.indexOf(fieldName)
      if (currentIdx >= 0 && currentIdx < inviteCodeFields.length - 1) {
        const nextField = inviteCodeFields[currentIdx + 1]
        methods.setFocus(nextField)
        methods.resetField(nextField)
      }
    }
  }

  const handleKeyDown =
    (name: InviteCodeField) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIdx = inviteCodeFields.indexOf(name)
      const currentValue = methods.getValues(name)

      if (e.key === "Backspace") {
        if (!currentValue && currentIdx > 0) {
          const prevField = inviteCodeFields[currentIdx - 1]
          methods.setValue(prevField, "")
          setInviteCode((prev) => ({ ...prev, [prevField]: "" }))
          methods.setFocus(prevField)
        }
        return
      }

      if (regexAlphaNumericKey.test(e.key) && currentValue) {
        methods.setValue(name, "")
        setInviteCode((prev) => ({ ...prev, [name]: "" }))
      }
    }

  const handlePaste =
    (name: InviteCodeField) => (e: React.ClipboardEvent<HTMLDivElement>) => {
      const pasted = e.clipboardData.getData("text").trim()
      const sanitizedPasted = sanitizeInviteCode(pasted)
      const currentIdx = inviteCodeFields.indexOf(name)

      if (!sanitizedPasted.length) {
        e.preventDefault()
        return
      }

      e.preventDefault()

      if (sanitizedPasted.length === 1) {
        methods.setValue(name, sanitizedPasted)
        setInviteCode((prev) => ({ ...prev, [name]: sanitizedPasted }))
        return
      }

      const startIdx = currentIdx
      if (startIdx < 0) return

      const updates: Partial<Record<InviteCodeField, string>> = {}

      for (let i = 0; i < inviteCodeFields.length; i++) {
        const charIdx = i - startIdx
        if (charIdx >= 0 && charIdx < sanitizedPasted.length) {
          const char = sanitizedPasted[charIdx]
          methods.setValue(inviteCodeFields[i], char)
          updates[inviteCodeFields[i]] = char
        }
      }

      setInviteCode((prev) => ({ ...prev, ...updates }))

      const lastFilledIdx = Math.min(
        startIdx + sanitizedPasted.length - 1,
        inviteCodeFields.length - 1
      )
      methods.setFocus(inviteCodeFields[lastFilledIdx])

      if (startIdx === 0 && lastFilledIdx === inviteCodeFields.length - 1) {
        setPasteAnnouncement(t("noAuthFlow.classCodeFilled"))
      }
    }

  return (
    <Stack>
      {label && (
        <Typography
          id={labelId}
          mb="8px"
          variant="body3"
          fontWeight={600}
          color="mockup.neutral40"
        >
          {label}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing="12px"
        role="group"
        aria-labelledby={label ? labelId : undefined}
      >
        {inviteCodeFields.map((field) => (
          <TextField
            key={field}
            name={field}
            inputProps={{
              maxLength: 1,
              style: { textAlign: "center" },
            }}
            onPaste={handlePaste(field)}
            onKeyDown={handleKeyDown(field)}
            customOnChange={(e, onChange) => {
              const sanitizedValue = sanitizeInviteCode(e.target.value).slice(
                0,
                1
              )
              onChange(sanitizedValue)
              handleGetNumber(sanitizedValue, e.target.name as InviteCodeField)
            }}
          />
        ))}
      </Stack>
      <Box sx={visuallyHiddenSx} aria-live="polite">
        {pasteAnnouncement}
      </Box>
    </Stack>
  )
}

export default ClassCode
